import React from 'react';
import { Badge, HStack, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { CLIENTS_BY_PERMISSIONS } from '../pages/clients/queries';

/**
 * Shared helpers for the dev-mode Shift+hover permission reveal.
 * See `vault/how-to/dev-mode-permission-hover.md`.
 *
 * Two pieces:
 *   - `renderPermissionTooltip(args)` — multi-line tooltip body (permission
 *     level, resolved perms, path, module, clients-with-access row).
 *   - `ClientsWithAccessRow` — fires `clientsByPermissions` when the tooltip
 *     opens and lists tenant clients whose permissions intersect `resolved`.
 *     Apollo's normalised cache dedupes repeat hovers.
 *
 * Only render these inside a tooltip whose parent already gates on
 * `isDevMode() && useShiftKey()`.
 */

const CLIENT_LIST_CAP = 20;

interface TenantClient {
  id: string;
  fName?: string | null;
  lName?: string | null;
  email?: string | null;
}

export function ClientsWithAccessRow({ permissions }: { permissions: string[] }) {
  const { data, loading, error } = useQuery<{ clientsByPermissions: TenantClient[] }>(
    CLIENTS_BY_PERMISSIONS,
    {
      variables: { permissions, limit: 100 },
      skip: permissions.length === 0,
      fetchPolicy: 'cache-first',
    }
  );

  if (permissions.length === 0) {
    return <Text color="gray.400">(public — anyone can access)</Text>;
  }
  if (loading) return <Text color="gray.400">loading clients…</Text>;
  if (error) return <Text color="red.300">error: {error.message}</Text>;

  const clients = data?.clientsByPermissions ?? [];
  if (clients.length === 0) {
    return <Text color="orange.300">no clients on this tenant have access</Text>;
  }

  const display = (c: TenantClient) =>
    [c.fName, c.lName].filter(Boolean).join(' ') || c.email || c.id;
  const sorted = [...clients].sort((a, b) => display(a).localeCompare(display(b)));
  const shown = sorted.slice(0, CLIENT_LIST_CAP);
  const overflow = sorted.length - shown.length;

  return (
    <VStack align="start" spacing={0} w="100%">
      <Text color="gray.300">clients with access ({clients.length}):</Text>
      <Text>
        {shown.map(display).join(', ')}
        {overflow > 0 ? `, +${overflow} more` : ''}
      </Text>
    </VStack>
  );
}

export function renderPermissionTooltip(args: {
  label: string;
  path: string;
  moduleId: string;
  permissionLevel?: string;
  resolved: string[];
}) {
  const { label, path, moduleId, permissionLevel, resolved } = args;
  return (
    <VStack align="start" spacing={1} fontSize="xs" minW="240px" maxW="360px">
      <Text fontWeight="bold">{label}</Text>
      <HStack spacing={1} wrap="wrap">
        <Text>🔑</Text>
        <Text>level:</Text>
        {permissionLevel ? (
          <Badge colorScheme="blue" fontSize="0.65em">{permissionLevel}</Badge>
        ) : (
          <Text color="gray.400">(public / no permission)</Text>
        )}
      </HStack>
      {resolved.length > 0 && (
        <HStack align="start" spacing={1} wrap="wrap">
          <Text>→</Text>
          <HStack spacing={1} wrap="wrap">
            {resolved.map((p) => (
              <Badge key={p} colorScheme="purple" fontSize="0.65em">{p}</Badge>
            ))}
          </HStack>
        </HStack>
      )}
      <Text color="gray.300">path: {path}</Text>
      <Text color="gray.300">module: {moduleId}</Text>
      <ClientsWithAccessRow permissions={resolved} />
    </VStack>
  );
}
